import { useEffect, useRef } from 'react';
import CloseIcon from '../Icons/CloseIcon';
import ArrowBack from '../Icons/ArrowBack';

export interface ModalMobileProps {
  visible?: boolean;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  height?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  hideHeader?: boolean;
  icon?: React.ReactNode;
  id?: string;
  btnCloseTestId?: string;
  scrollBehaviour?: boolean;
  showOnLargeScreen?: boolean;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  modalClassName?: string;
  headerClassName?: string;
  titleHeaderClassName?: string;
  sectionClassName?: string;
  isProgressBar?: boolean;
  progressBarProgress?: number;
  isHeight?: boolean;
  heightTopSection?: string; // New prop for height of the top section
}

const ModalMobile = ({
  children,
  icon,
  btnCloseTestId,
  scrollBehaviour,
  showOnLargeScreen = false,
  showBackButton = false,
  showCloseButton = true,
  visible = false,
  title = '',
  subTitle = '',
  id = 'formContainer',
  height = '50vh',
  bodyClassName = '',
  onClose = () => {},
  hideHeader = false,
  modalClassName = '',
  headerClassName = '',
  titleHeaderClassName = '',
  sectionClassName = '',
  isProgressBar = false,
  progressBarProgress = 0,
  isHeight = true,
  heightTopSection = '', // Default height for the top section
}: ModalMobileProps) => {
  const refModal = useRef(null);
  const backdropModalMobileRef = useRef(null);

  useEffect(() => {
    const closeOpenMenus = (e: MouseEvent) => {
      if (
        visible &&
        !refModal.current?.contains(e.target as Node) &&
        e.target === backdropModalMobileRef.current
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', closeOpenMenus);

    return () => {
      document.removeEventListener('mousedown', closeOpenMenus);
    };
  }, [visible, onClose]);

  useEffect(() => {
    if (visible) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.style.height = '100vh';
    }

    return () => {
      if (visible) {
        document.body.style.overflow = 'unset';
        document.body.style.paddingRight = '0px';
        document.body.style.height = 'auto';
      }
    };
  }, [visible]);

  return (
    <div
      ref={backdropModalMobileRef}
      // double height for avoid when browser not support dvh
      className={`fixed top-0 left-0 w-screen ${heightTopSection ? heightTopSection : 'h-dvh'} h-screen z-[100] bg-black bg-opacity-30 ${
        visible ? 'block' : 'hidden'
      } ${modalClassName}`}
    >
      {visible && (
        <section
          id={id}
          ref={refModal}
          className={
            `${
              scrollBehaviour ? 'overflow-y-auto' : 'fixed'
            } ${isHeight ? 'h-dvh' : ''} flex-col z-[71] bottom-0 left-0 items-center justify-center w-full bg-white shadow-xl delay-400 duration-300 ease-in-out transition-all transform ${showOnLargeScreen ? 'xl:hidden' : 'lg:hidden'} ${sectionClassName} ` +
            (visible ? `translate-y-0 ${height}` : ' translate-y-full h-0')
          }
        >
          {/* Header */}
          {!hideHeader && (
            <header
              className={`flex w-full ${showBackButton ? 'justify-start' : 'justify-between'} gap-4 p-4 ${!isProgressBar && 'mb-2'} border-b border-b-reliableBlack10 ${headerClassName}`}
            >
              {showBackButton && (
                <button
                  className="text-black mb-1"
                  aria-label="close-button"
                  onClick={onClose}
                  data-testid={btnCloseTestId}
                >
                  <ArrowBack size={15} color="#000" />
                </button>
              )}

              <div>
                <h3
                  className={`text-base font-bold text-reliableBlack90 uppercase flex gap-2 items-center ${titleHeaderClassName}`}
                >
                  {icon}
                  <div className="flex-1 line-clamp-2 break-all">{title}</div>
                </h3>
                <p className="text-xs font-normal text-reliableBlack90">{subTitle}</p>
              </div>
              {showCloseButton && (
                <button
                  className="text-black"
                  aria-label="close-button"
                  onClick={onClose}
                  data-testid={btnCloseTestId}
                >
                  <CloseIcon size={24} fill="#000" />
                </button>
              )}
            </header>
          )}
          {isProgressBar && (
            <div className="w-full bg-gray-200 h-[3px] dark:bg-gray-700">
              <div
                className="bg-[#4cc0ad] h-[3px]"
                style={{ width: `${progressBarProgress}%` }}
              ></div>
            </div>
          )}
          {/* Body */}
          <main className={bodyClassName}>{children}</main>
        </section>
      )}
    </div>
  );
};

export default ModalMobile;
